/**
 * Text-selection → W3C TextQuoteSelector. The grader lets an operator select a
 * span of a rendered letter and annotate it; this turns the DOM Selection into a
 * re-anchorable {@link TextAnchor} (exact quote + prefix/suffix context + offset
 * fallback), exactly the shape rlhf-core validates.
 *
 * The offset math is a PURE function ({@link buildAnchor}) so it is unit-testable
 * with no DOM; {@link anchorFromRange}/{@link anchorFromSelection} are the thin DOM
 * adapters (a real Range gives us the text before the selection and the exact quote).
 * Elements render into LIGHT DOM (the web-kit KitElement contract), so
 * `window.getSelection()` sees the text with no shadow boundary in the way.
 */

import type { TextAnchor } from "@platform/rlhf-core"

/** How much surrounding context to capture on each side (chars). */
const CONTEXT = 32

/**
 * Build a TextAnchor from the full rendered text, the text BEFORE the selection,
 * and the exact selected quote. Pure — the offset/context derivation, no DOM.
 */
export function buildAnchor(fullText: string, before: string, exact: string): TextAnchor | null {
  if (!exact) return null
  const start = before.length
  const end = start + exact.length
  const prefix = before.slice(-CONTEXT)
  const suffix = fullText.slice(end, end + CONTEXT)
  const anchor: TextAnchor = { exact, start, end }
  if (prefix) anchor.prefix = prefix
  if (suffix) anchor.suffix = suffix
  return anchor
}

/** Adapt a DOM Range within `root` into a TextAnchor (null when empty / outside). */
export function anchorFromRange(root: Element, range: Range): TextAnchor | null {
  const exact = range.toString()
  if (!exact || !root.contains(range.commonAncestorContainer)) return null
  const pre = range.cloneRange()
  pre.selectNodeContents(root)
  pre.setEnd(range.startContainer, range.startOffset)
  return buildAnchor(root.textContent ?? "", pre.toString(), exact)
}

/** Read the current document Selection and anchor it within `root` (null when none). */
export function anchorFromSelection(root: Element): TextAnchor | null {
  const view = root.ownerDocument?.defaultView ?? (globalThis as unknown as Window)
  const selection = view.getSelection?.()
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return null
  return anchorFromRange(root, selection.getRangeAt(0))
}
